import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { enumLabel } from "@/i18n/enumLabels";
import { api, openAuthedFile, uploadFormData } from "@/api/client";
import { DOCUMENT_KIND_LABELS, type DocumentKind, type DocumentStatus } from "@ckgo2/shared/types";
import { formatDate } from "@/lib/format";
import SignStatusBadge from "@/components/common/SignStatusBadge";
import {
  FileText,
  FileSignature,
  Map,
  Download,
  Loader2,
  Send,
  Copy,
  Check,
  ShieldCheck,
  Umbrella,
} from "lucide-react";

interface OfferWarning {
  level: "error" | "warning";
  message: string;
}

interface DocRow {
  id: string;
  title: string;
  number?: string | null;
  kind: DocumentKind;
  status: DocumentStatus;
  requiresSignature: boolean;
  r2Key?: string | null;
  createdAt: string;
  signedAt?: string | null;
}

/**
 * Documents for an order (Block 2 B2.4). Generate a contract (rendered via Browser
 * Rendering, persisted to R2), open the authed PDF, and send it for e-signature — the
 * public sign link is shown for copy/paste in addition to the emailed link.
 */
export default function DocumentsPanel({ orderId }: { orderId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);
  const [signLinks, setSignLinks] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState<string | null>(null);
  const [offerWarnings, setOfferWarnings] = useState<OfferWarning[] | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["order-documents", orderId],
    queryFn: () => api.get<{ data: DocRow[] }>(`/api/admin/documents?orderId=${orderId}&limit=50`),
  });

  const generate = useMutation({
    mutationFn: (vars: { kind: DocumentKind; requiresSignature: boolean }) =>
      api.post<{ data: { warnings?: OfferWarning[] } }>(`/api/admin/documents/generate`, {
        orderId,
        ...vars,
      }),
    onSuccess: (res, vars) => {
      // Offer generation returns its deterministic consistency warnings inline.
      if (vars.kind === "offer") setOfferWarnings(res.data?.warnings ?? []);
      qc.invalidateQueries({ queryKey: ["order-documents", orderId] });
    },
  });

  // Block 5.6 Řez 2 — pre-send consistency check (rules + AI pass). Advisory only.
  const offerCheck = useMutation({
    mutationFn: () =>
      api.post<{ data: { warnings: OfferWarning[] } }>(`/api/admin/documents/offer-check`, {
        orderId,
      }),
    onSuccess: (res) => setOfferWarnings(res.data?.warnings ?? []),
  });

  const send = useMutation({
    mutationFn: (id: string) =>
      api.post<{ data: { signUrl: string } }>(`/api/admin/documents/${id}/send`, {}),
    onSuccess: (res, id) => {
      setSignLinks((m) => ({ ...m, [id]: res.data.signUrl }));
      qc.invalidateQueries({ queryKey: ["order-documents", orderId] });
    },
  });

  // Upload the insurer's policy PDF (Block 6.1 Řez 2c) — an uploaded artifact, not a generated doc.
  const uploadInsurance = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("orderId", orderId);
      return uploadFormData(`/api/admin/documents/upload-insurance`, fd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["order-documents", orderId] }),
  });

  const openPdf = async (id: string) => {
    setPdfBusy(id);
    try {
      await openAuthedFile(`/api/admin/documents/${id}/pdf`);
    } finally {
      setPdfBusy(null);
    }
  };

  const copyLink = (id: string, url: string) => {
    void navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied((v) => (v === id ? null : v)), 1500);
  };

  const docs = data?.data ?? [];

  return (
    <div className="card">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <FileSignature className="h-5 w-5" /> {t("orders.documents.title")}
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => generate.mutate({ kind: "contract", requiresSignature: true })}
            disabled={generate.isPending}
            className="btn-ghost btn-sm"
          >
            {generate.isPending && generate.variables?.kind === "contract" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}{" "}
            {t("orders.documents.contract")}
          </button>
          <button
            onClick={() => generate.mutate({ kind: "itinerary", requiresSignature: false })}
            disabled={generate.isPending}
            className="btn-ghost btn-sm"
          >
            <Map className="h-4 w-4" /> {t("orders.documents.itinerary")}
          </button>
          <button
            onClick={() => generate.mutate({ kind: "offer", requiresSignature: false })}
            disabled={generate.isPending}
            className="btn-ghost btn-sm"
            title={t("orders.documents.offerTitle")}
          >
            {generate.isPending && generate.variables?.kind === "offer" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}{" "}
            {t("orders.documents.offerPdf")}
          </button>
          <button
            onClick={() => offerCheck.mutate()}
            disabled={offerCheck.isPending}
            className="btn-ghost btn-sm"
            title={t("orders.documents.checkTitle")}
          >
            {offerCheck.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ShieldCheck className="h-4 w-4" />
            )}{" "}
            {t("orders.documents.check")}
          </button>
          <label
            className={`btn-ghost btn-sm inline-flex cursor-pointer ${
              uploadInsurance.isPending ? "pointer-events-none opacity-50" : ""
            }`}
            title={t("orders.documents.insuranceTitle")}
          >
            {uploadInsurance.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Umbrella className="h-4 w-4" />
            )}{" "}
            {t("orders.documents.insurancePdf")}
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              disabled={uploadInsurance.isPending}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadInsurance.mutate(f);
                e.target.value = "";
              }}
            />
          </label>
        </div>
      </div>

      {offerWarnings !== null && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-900/20">
          {offerWarnings.length === 0 ? (
            <p className="text-green-700 dark:text-green-400">
              {t("orders.documents.offerConsistent")}
            </p>
          ) : (
            <ul className="space-y-1">
              {offerWarnings.map((w, i) => (
                <li
                  key={i}
                  className={
                    w.level === "error"
                      ? "text-red-700 dark:text-red-400"
                      : "text-amber-800 dark:text-amber-300"
                  }
                >
                  {w.level === "error" ? "✖" : "⚠"} {w.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> {t("common.states.loading")}
        </div>
      ) : docs.length === 0 ? (
        <p className="py-3 text-sm text-gray-400">{t("orders.documents.empty")}</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {docs.map((d) => (
            <li key={d.id} className="py-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {d.number ? <span className="font-mono">{d.number}</span> : null}{" "}
                      <span className="text-gray-500">
                        {enumLabel(DOCUMENT_KIND_LABELS, d.kind)}
                      </span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {t("orders.documents.createdAt", { date: formatDate(d.createdAt) })}
                      {d.signedAt && (
                        <span className="text-emerald-600 dark:text-emerald-400">
                          {" · "}
                          {t("orders.documents.signedAt", { date: formatDate(d.signedAt) })}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <SignStatusBadge status={d.status} />
                  {d.requiresSignature && d.status !== "signed" && d.status !== "void" && (
                    <button
                      onClick={() => send.mutate(d.id)}
                      disabled={send.isPending}
                      className="btn-ghost btn-sm"
                      title={t("orders.documents.sendForSignature")}
                    >
                      {send.isPending && send.variables === d.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => openPdf(d.id)}
                    className="btn-ghost btn-sm"
                    title={t("orders.documents.openPdf")}
                    disabled={pdfBusy === d.id || !d.r2Key}
                  >
                    {pdfBusy === d.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              {signLinks[d.id] && (
                <div className="mt-2 flex items-center gap-2 rounded bg-gray-50 px-2 py-1.5 dark:bg-gray-800">
                  <input
                    readOnly
                    value={signLinks[d.id]}
                    className="flex-1 bg-transparent font-mono text-xs text-gray-600 dark:text-gray-300"
                  />
                  <button
                    onClick={() => copyLink(d.id, signLinks[d.id])}
                    className="btn-ghost btn-sm"
                    title={t("orders.documents.copySignLink")}
                  >
                    {copied === d.id ? (
                      <Check className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
